import { http } from '@/helpers/easyindustriaapi/config';
import { RRule } from "rrule";
import moment from "moment";
import Swal from "sweetalert2";
import { agendaEndOnStartDayIso, parseAgendaDateOnly, weeklySeriesAnchorMoment } from '@/controller/agendaEventTime';

/** ISO-8601 weekday N (Carbon): 1=seg … 7=dom → FullCalendar `daysOfWeek`: 0=dom … 6=sáb */
function isoDayOfWeekToFullCalendar(iso) {
  if (iso === null || iso === undefined || iso === '') return null
  const n = parseInt(String(iso), 10)
  if (Number.isNaN(n)) return null
  // Tolerância: se a origem já for 0..6 (padrão FullCalendar), devolvemos direto.
  if (n >= 0 && n <= 6) return n
  // Caso ISO 1..7 (Carbon): 7=domingo -> FullCalendar 0=domingo
  if (n >= 1 && n <= 7) return n === 7 ? 0 : n
  return null
}

/** FullCalendar dow 0=dom … 6=sáb → constante `byweekday` do rrule.js (MO=0 … SU=6 no índice interno) */
function fullCalendarDowToRRuleWeekday(fcDow) {
  const map = [RRule.SU, RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA]
  if (fcDow == null || fcDow < 0 || fcDow > 6) return null
  return map[fcDow]
}

export default class CalendarController {
  constructor() {
    this.currentUser    = JSON.parse(localStorage.getItem('user'))
    this.currentEmpresa = JSON.parse(localStorage.getItem('currentEmpresa'))
  }

  /**
   * Normaliza data+hora em ISO local (`YYYY-MM-DDTHH:mm:ss`) para o FullCalendar.
   * Evita deslocamentos de timezone ao extrair apenas a data e aplica fallback de hora.
   *
   * @param {string|Date|null|undefined} dateInput Data de referência (agenda/start/start_recur).
   * @param {string|null|undefined} timeInput Hora opcional (`HH:mm` ou `HH:mm:ss`).
   * @returns {string} DateTime ISO seguro para `start`, `end` e `rrule.dtstart`.
   */
  formatEventDateTime(dateInput, timeInput) {
    const day = parseAgendaDateOnly(dateInput)
    const fallbackBase = moment(dateInput)
    const base = day.isValid()
      ? day
      : (fallbackBase.isValid() ? fallbackBase.clone().startOf('day') : moment().startOf('day'))

    const rawTime = String(timeInput == null || String(timeInput).trim() === '' ? '00:00:00' : timeInput).trim()
    const normalizedTime = rawTime.length <= 5 ? `${rawTime}:00` : rawTime
    const parsedTime = moment(normalizedTime, ['HH:mm:ss', 'H:mm:ss', 'HH:mm'], true)

    if (!parsedTime.isValid()) {
      return base.clone().hour(0).minute(0).second(0).millisecond(0).format('YYYY-MM-DDTHH:mm:ss')
    }

    return base
      .clone()
      .hour(parsedTime.hour())
      .minute(parsedTime.minute())
      .second(parsedTime.second())
      .millisecond(0)
      .format('YYYY-MM-DDTHH:mm:ss')
  }

  lumine(rgb) {
    let color = '#FFFFFF';
      var nib = rgb.split(''); 
      var r = parseInt(nib[1]+nib[2],16);
      var g = parseInt(nib[3]+nib[4],16);
      var b = parseInt(nib[5]+nib[6],16);
      var lumin = ( r * 299 + g * 587 + b * 114) / 1000;
      
      if (lumin > 128) {
        color = '#000000';
      }

      return color;
  }

  eventsResource(events) {
    Swal.fire({
      title: "Aguarde...",
      text: "Preparando tela da agenda...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(); // Exibe o loader enquanto a requisição está em andamento
      },
    });
    const processedEvents = [];
    events.forEach(event => {
      const recurrenceMode = (event.recurrence || '').toUpperCase()
      const isRecurringAgenda = ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(recurrenceMode)
      const startDateTimeIso = this.formatEventDateTime(event.start, event.start_time)
      const endDateTimeIso = agendaEndOnStartDayIso(startDateTimeIso, event.end_time)

      if (isRecurringAgenda) {
        let calendar = {
          id: event.id,
          title: event.professional.name + " - " + event.pacient.nome,
          start: startDateTimeIso,
          end: endDateTimeIso,
          allDay: false,
          startTime: event.start_time,
          endTime: event.end_time,
          daysOfWeek: [event.day_of_week],
          startRecur: event.start_recur,
          endRecur: event.end_recur,
          color: (event.professional.color) ? event.professional.color : "#0000ff",
          textColor: (event.professional.color) ? this.lumine(event.professional.color) : '#ffffff',
          editable: (event.status == 1),
          extendedProps: {
            empresa_id: event.empresa_id,
            professional: event.professional,
            pacients: event.pacient,
            pacient: event.pacient,
            pacient_id: event.pacient_id,
            professional_id: event.professional_id,
            specialty_id: event.specialty_id,
            description: event.desc,
            atendimento_id: (event.atendimento_id) ? event.atendimento_id : 0,
            recurrences: event.recurrences,
            status: event.status
          },
          display: "block",
        };

        // Error in toUpperCase
        if (event.recurrences && Array.isArray(event.recurrences) && event.recurrences.length > 0)
        {
          const excludeDates = event.recurrences
            .filter((recurrence) => recurrence.is_cancelled === 1) // Filtra apenas os cancelados
            .map((recurrence) => this.formatEventDateTime(recurrence.start, recurrence.start_time));
          calendar.exdate = excludeDates;
        }

        // Switch case event.recurrence
        switch ((event.recurrence || '').toUpperCase()) {
          case 'DAILY':
            {
              const untilMoment = moment(event.end_recur)
              const rruleConfig = {
                freq: event.recurrence,
                dtstart: moment(startDateTimeIso, 'YYYY-MM-DDTHH:mm:ss', true).toDate(),
              }
              if (untilMoment.isValid()) {
                rruleConfig.until = untilMoment.endOf('day').toDate()
              }
              calendar.rrule = rruleConfig
            }
            break;
          case 'WEEKLY':
            {
              const untilMoment = moment(event.end_recur)
              const rruleConfig = {
                freq: event.recurrence,
                dtstart: moment(startDateTimeIso, 'YYYY-MM-DDTHH:mm:ss', true).toDate(),
              }
              if (untilMoment.isValid()) {
                rruleConfig.until = untilMoment.endOf('day').toDate()
              }
              calendar.rrule = rruleConfig
            }
            break;
          case 'MONTHLY':
            {
              const untilMoment = moment(event.end_recur)
              const rruleConfig = {
                freq: event.recurrence,
                interval: 1,
                dtstart: moment(startDateTimeIso, 'YYYY-MM-DDTHH:mm:ss', true).toDate(),
                bymonthday: parseInt(moment(startDateTimeIso).format('DD')), // específico para o dia
              }
              if (untilMoment.isValid()) {
                rruleConfig.until = untilMoment.endOf('day').toDate()
              }
              calendar.rrule = rruleConfig
            }
            break;
          case 'YEARLY':
            {
              const untilMoment = moment(event.end_recur)
              const rruleConfig = {
                freq: 'YEARLY',
                interval: 1,
                dtstart: moment(startDateTimeIso, 'YYYY-MM-DDTHH:mm:ss', true).toDate(),
                bymonth: parseInt(moment(startDateTimeIso).format('MM')), // específico para o mês
                bymonthday: parseInt(moment(startDateTimeIso).format('DD')),
              }
              if (untilMoment.isValid()) {
                rruleConfig.until = untilMoment.endOf('day').toDate()
              }
              calendar.rrule = rruleConfig
            }
            break;
          default: {
            console.log('Default');
          }
        }

        // Test if exist professionals set color and title
        if (event.professionals && event.professionals.length > 0)
          {
            calendar.professionals = event.professionals;
            calendar.color = '#000000';
            calendar.textColor = '#FFFFFF';
            calendar.title = "REUNIÃO - " + event.pacient.nome;
            calendar.meet = true;
          }

        processedEvents.push({ ...calendar });
      } else {
        // Eventos não-recorrentes (NONE, vazio, reposição): datetime ISO local + allDay false.
        let calendar = {
          id: event.id,
          title: event.professional.name + " - " + event.pacient.nome,
          start: startDateTimeIso,
          end: endDateTimeIso,
          allDay: false,
          color: (event.professional.color) ? event.professional.color : "#0000ff",
          textColor: (event.professional.color) ? this.lumine(event.professional.color) : '#ffffff',
          editable: (event.status == 1),
          extendedProps: {
            empresa_id: event.empresa_id,
            professional: event.professional,
            pacients: event.pacient,
            pacient: event.pacient,
            pacient_id: event.pacient_id,
            professional_id: event.professional_id,
            specialty_id: event.specialty_id,
            description: event.desc,
            atendimento_id: (event.atendimento_id) ? event.atendimento_id : 0,
            recurrences: event.recurrences,
            status: event.status
          },
          display: "block",
        };

        if (event.professionals && event.professionals.length > 0)
          {
            calendar.professionals = event.professionals;
            calendar.color = '#000000';
            calendar.textColor = '#FFFFFF';
            calendar.title = "REUNIÃO - " + event.pacient.nome;
            calendar.meet = true;
          }

        processedEvents.push({ ...calendar });
      }
    });

    return processedEvents;
  }

  async getEvents(axiosParams) {
    Swal.fire({
      title: "Aguarde...",
      text: "Carregando atendimentos da agenda...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(); // Exibe o loader enquanto a requisição está em andamento
      },
    });

    try {
      const promisse = http.get("/agenda", { params: axiosParams });
      return promisse
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          console.log(error);
          Swal.fire({
            title: "Atenção",
            text: "Um erro ocorreu ao carregar os atendimentos: \n" + error.response,
            allowOutsideClick: false,
          });
        })
        .finally(() => {
        });
    } catch (error) {
      console.error("Erro ao buscar eventos:", error);
      return [];
    }
  }

  async loadEvents(axiosParams) {
    try {
      const promisse = http.get("/agenda", { params: axiosParams });
      promisse.then((response) => {
        const data = response.data;
        const processedEvents = [];

        data.forEach(event => {
          // Adicionar o evento principal
          let calendario = {
            id: event.id,
            title: event.professional.name + " - " + event.pacient.nome,
            start: moment(event.start).format("yyyy-MM-DD"),
            end: (event.end) ? moment(event.end).format("yyyy-MM-DD") : null,
            color: event.professional?.color || "#0000ff", // Cor do profissional
            extendedProps: {
              desc: event.desc,
              professional: event.professional,
              pacient: event.pacient,
            },
          };
          
          processedEvents.push({ ...calendario });

          // Adicionar as recorrências
          if (event.recurrence && event.start_recur) {
            const rule = new RRule({
              freq: RRule[event.recurrence], // DAILY, WEEKLY, etc.
              dtstart: new Date(event.start_recur),
              until: event.end_recur ? new Date(event.end_recur) : null,
              byweekday: event.day_of_week !== null ? [event.day_of_week] : null,
            });

            rule.all().forEach(date => {
              // Verificar se há recorrências personalizadas para esta data
              const recurrence = event.recurrences.find(r =>
                new Date(r.start).toISOString().split("T")[0] === date.toISOString().split("T")[0]
              );

              processedEvents.push({
                id: `${event.id}-recurrence-${date.toISOString()}`,
                title: event.title,
                start: `${date.toISOString().split("T")[0]}T${recurrence?.start_time || event.start_time}`,
                end: `${date.toISOString().split("T")[0]}T${recurrence?.end_time || event.end_time}`,
                backgroundColor: event.professional?.color || "#ccc",
                extendedProps: {
                  desc: recurrence?.desc || event.desc,
                  professional: event.professional,
                  pacient: event.pacient,
                },
              });
            });
          }
        });

        return processedEvents;
      });
    } catch (error) {
      console.error("Erro ao carregar os eventos:", error);
      throw error;
    }
  }

  async getProfessionalCalendar(axiosParams) {
    Swal.fire({
      title: "Aguarde...",
      text: "Carregando atendimentos da agenda...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(); // Exibe o loader enquanto a requisição está em andamento
      },
    });

    try {
      const promisse = http.get("/agenda/profissional", { params: axiosParams });
      return promisse.then((response) => {
        Swal.close();
        return response.data;
      });
    } catch (error) {
      console.error("Erro ao buscar eventos:", error);
      return [];
    }
  }

  async getCustomerCalendar(axiosParams) {
    Swal.fire({
      title: "Aguarde...",
      text: "Carregando atendimentos da agenda...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(); // Exibe o loader enquanto a requisição está em andamento
      },
    });

    try {
      const promisse = http.get("/agenda/customer", { params: axiosParams });
      return promisse.then((response) => {
        Swal.close();
        return response.data;
      });
    } catch (error) {
      console.error("Erro ao buscar eventos:", error);
      return [];
    }
  }

  eventsProfessionalResource(events) {
    let vet_aux = []
    events.forEach((e) => {
      const recurrenceMode = (e.recurrence || '').toUpperCase()
      const startDateTimeIso = this.formatEventDateTime(e.start, e.start_time)
      const weeklyAnchor = recurrenceMode === 'WEEKLY' ? weeklySeriesAnchorMoment(e) : null
      const endDateTimeIso = agendaEndOnStartDayIso(startDateTimeIso, e.end_time)

      let specialty
      
      if (e.atendimento !== null && e.atendimento.specialty)
      {
        specialty = ' - ' + e.atendimento.specialty.name.slice(0, 15)
      } else {
        specialty = ''
      }

      // FullCalendar: para eventos com rrule, usamos apenas rrule.
      // NÃO definir daysOfWeek/startTime/endTime/startRecur/endRecur no objeto,
      // pois o FullCalendar processa rrule E simpleRecurring em paralelo,
      // gerando eventos duplicados ou expandindo infinitamente.
      let objeto = {
        id: e.id,
        minTime: '07:00:00',
        allDay: false,
        title: ((e.pacient) ? ' '+e.pacient.nome.slice(0, 15) : ' ') + specialty,
        color: "#4aa3ff", // Cor de fundo azul
        textColor: '#000000', // Texto branco
        extendedProps: {
          empresa_id: e.empresa_id,
          professional: e.professional,
          pacients: e.pacient,
          pacient_id: e.pacient_id,
          professional_id: e.professional_id,
          specialty_id: e.specialty_id,
          description: e.desc,
          atendimento_id: (e.atendimento_id) ? e.atendimento_id : 0,
          recurrences: e.recurrences,
          atendimento: e.atendimento,
          day_of_week: e.day_of_week,
          recurrence: e.recurrence,
          start_recur: e.start_recur,
          end_recur: e.end_recur,
          treatment: e.treatment,
          approach: e.approach,
          agreement: e.agreement,
          startTime: e.start_time,
          endTime: e.end_time,
        },
        display: "block",
      }

      if (recurrenceMode === 'WEEKLY') {
        // Para WEEKLY usamos rrule (como DAILY/MONTHLY/YEARLY), não simpleRecurring.
        // simpleRecurring com daysOfWeek sem startRecur/endRecur expande para
        // todo o período visível, causando repetição infinita.
        // Importante: NÃO definir daysOfWeek/startTime/endTime/startRecur/endRecur aqui,
        // pois o FullCalendar processa rrule E simpleRecurring em paralelo, gerando
        // eventos duplicados. Apenas rrule resolve.
        // `start`/`end` do objeto seguem a data de ancoragem (`start_recur` quando existir),
        // alinhando com o dtstart do rrule (ex.: agenda 8231 deve iniciar em 15/05/2026).
        const weeklyStartIso = this.formatEventDateTime(weeklyAnchor.format('YYYY-MM-DD'), e.start_time)
        objeto.start = weeklyStartIso
        objeto.end = agendaEndOnStartDayIso(weeklyStartIso, e.end_time)
      } else if (['DAILY', 'MONTHLY', 'YEARLY'].includes(recurrenceMode)) {
        objeto.start = startDateTimeIso
        objeto.end = endDateTimeIso
      } else {
        // NONE, vazio, reposição: manter datetime ISO completo evita renderização em all-day.
        objeto.start = startDateTimeIso
        objeto.end = endDateTimeIso
      }

      // Error in toUpperCase
      if (e.recurrences && Array.isArray(e.recurrences) && e.recurrences.length > 0)
      {
        const excludeDates = e.recurrences
          .filter((recurrence) => recurrence.is_cancelled === 1) // Filtra apenas os cancelados
          .map((recurrence) => this.formatEventDateTime(recurrence.start, recurrence.start_time))
        objeto.exdate = excludeDates
      }

      // Switch case event.recurrence
      switch ((e.recurrence || '').toUpperCase()) {
        case 'DAILY':
          {
            const dtDaily = moment(startDateTimeIso, 'YYYY-MM-DDTHH:mm:ss', true).toDate()
            const untilMoment = moment(e.end_recur)
            const rruleConfig = {
              freq: e.recurrence,
              dtstart: dtDaily,
            }
            if (untilMoment.isValid()) {
              rruleConfig.until = untilMoment.endOf('day').toDate()
            }
            objeto.rrule = rruleConfig
            break
          }
        case 'WEEKLY':
          {
            const weeklyStartIsoForRrule = this.formatEventDateTime(weeklyAnchor.format('YYYY-MM-DD'), e.start_time)
            const dtWeekly = moment(weeklyStartIsoForRrule, 'YYYY-MM-DDTHH:mm:ss', true).toDate()
            const untilMoment = moment(e.end_recur)
            const rruleConfig = {
              freq: e.recurrence,
              interval: 1,
              dtstart: dtWeekly,
            }
            if (untilMoment.isValid()) {
              rruleConfig.until = untilMoment.endOf('day').toDate()
            }
            const fcDow = isoDayOfWeekToFullCalendar(e.day_of_week) ?? weeklyAnchor.day()
            const rruleWday = fullCalendarDowToRRuleWeekday(fcDow)
            if (rruleWday) {
              rruleConfig.byweekday = [rruleWday]
            }
            objeto.rrule = rruleConfig
            break
          }
        case 'MONTHLY':
          {
            const dtMonthly = moment(startDateTimeIso, 'YYYY-MM-DDTHH:mm:ss', true).toDate()
            const untilMoment = moment(e.end_recur)
            const rruleConfig = {
              freq: e.recurrence,
              interval: 1,
              dtstart: dtMonthly,
              bymonthday: parseInt(moment(startDateTimeIso).format('DD')), // específico para o dia
            }
            if (untilMoment.isValid()) {
              rruleConfig.until = untilMoment.endOf('day').toDate()
            }
            objeto.rrule = rruleConfig
            break
          }
        case 'YEARLY':
          {
            const dtYearly = moment(startDateTimeIso, 'YYYY-MM-DDTHH:mm:ss', true).toDate()
            const untilMoment = moment(e.end_recur)
            const rruleConfig = {
              freq: 'YEARLY',
              interval: 1,
              dtstart: dtYearly,
              bymonth: parseInt(moment(startDateTimeIso).format('MM')), // específico para o mês
              bymonthday: parseInt(moment(startDateTimeIso).format('DD')),
            }
            if (untilMoment.isValid()) {
              rruleConfig.until = untilMoment.endOf('day').toDate()
            }
            objeto.rrule = rruleConfig
            break
          }
        default:
          break
      }

      if (e.professionals && e.professionals.length > 0)
      {
        objeto.professionals = e.professionals
        objeto.color = '#000000'
        objeto.textColor = '#FFFFFF'
        objeto.title = "REUNIÃO - " + e.pacient.nome
        objeto.meet = true
      }

      vet_aux.push({ ...objeto })
    }); // end foreach

    return vet_aux
  }

  async deleteRecurrence(recurrence) {
    const promisse = http.delete(`agenda/${recurrence.agenda_id}/recurrence/${recurrence.id}`)
    return promisse.then((response) => {
      return response
    })
  }
    
}
